import { ICommonSettings, ISessionSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const SESSION_SETTINGS_SEARCH_TERMS: string[] = [
  "Timeout (min)",
  "Logout countdown (sec)",
  "Persist session across page reloads"
];

export interface IConfigSessionSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ISessionSetting;
}

export interface IConfigSessionSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingSessionId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSessionSettings extends React.PureComponent<
  IConfigSessionSettingsProps,
  IConfigSessionSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSessionSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSessionSettingsProps & IWithSDProps) {
    if (prevProps.editingSessionId !== this.props.editingSessionId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingSessionId) return;

    const editingConfig = this.props.commonSettings.sessionSettings?.find(
      (x) => x.id === this.props.editingSessionId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const sessionSetting = this.props.commonSettings?.sessionSettings;
    if (!sessionSetting) return;

    const isSettingAlreadyExist =
      sessionSetting.some(
        (x) => x.name === (value && x.id !== this.props.editingSessionId && !x.isGlobalCommonConfigOnProjectConfig)
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Session Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ISessionSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      timeoutMins: data.timeoutMins,
      logoutCountdownSeconds: data.logoutCountdownSeconds,
      persistSessionAcrossPageReloads: data.persistSessionAcrossPageReloads
    };
    if (this.props.editingSessionId) {
      obj.id = this.props.editingSessionId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "sessionSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "sessionSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingSessionId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSessionId || "",
      configKey: "sessionSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Session setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Session setting"
        secondHeader="Session setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSessionId ? this.deleteSetting : undefined}
            deleteTestId="sessionSettingDeleteButton"
            submitTestId={`sessionSetting${this.props.editingSessionId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingSessionId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSessionForm"
          />
        }
      >
        <Form
          id="configSessionForm"
          name="sessionSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("web_sessionssettingpopup_description")}>
            <ConfigField label="Name" required tooltipContentKey="web_sessionssettingpopup_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Session Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Session Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="sessionSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Timeout (min)" tooltipContentKey="web_sessions_timeout">
              <Form.Item name="timeoutMins">
                <KuikaInput
                  size="md"
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === "-" || e.key === ",") e.preventDefault();
                  }}
                />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Logout countdown (sec)" tooltipContentKey="web_sessions_logoutcountdown">
              <Form.Item name="logoutCountdownSeconds">
                <KuikaInput
                  size="md"
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === "-" || e.key === ",") e.preventDefault();
                  }}
                />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Persist session across page reloads"
              tooltipContentKey="web_sessions_persist_session_across_page_reloads"
            >
              <Form.Item name="persistSessionAcrossPageReloads">
                <KuikaSwitch defaultChecked={this.state.editingConfig?.persistSessionAcrossPageReloads} />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
